
import { useEffect, useState } from "react";
import { useParams, Navigate } from "react-router-dom";
import { usePollDetails } from "@/hooks/polls/usePollDetails";
import { usePollDemographics } from "@/hooks/polls/usePollDemographics";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import PollPageHeader from "@/components/polls/PollPageHeader";
import PollRelatedInsights from "@/components/polls/PollRelatedInsights";
import { Helmet } from "react-helmet";
import { useSocialMetricsRealtime } from "@/hooks/useSocialMetricsRealtime";
import PollLoadingState from "@/components/polls/page/PollLoadingState";
import PollErrorState from "@/components/polls/page/PollErrorState";
import PollVotingCard from "@/components/polls/page/PollVotingCard";
import PollContentTabs from "@/components/polls/page/PollContentTabs";
//import PollAccessNotice from "@/components/polls/page/PollAccessNotice";
import { CommentCountProvider } from "@/contexts/comments/CommentCountContext";

const PollPage = () => {
  // Get both possible identifiers from the URL
  const { pollId, pollNumber } = useParams<{
    pollId?: string;
    pollNumber?: string;
  }>();
  const [activeTab, setActiveTab] = useState<string>("results");

  const {
    poll,
    isLoading,
    error,
    hasVoted,
    userVoteOptionId,
    canVote,
    cannotVoteReason,
    refresh,
    votesDistribution,
    relatedCards,
    canonicalUrl,
    shareableUrl,
    // Get the poll management state from usePollDetails
    polls,
    setPolls,
    userVotes,
    setUserVotes,
    markRecentVote,
  } = usePollDetails({
    pollId: pollId || undefined,
    pollNumber: pollNumber || undefined,
  });

  const {
    demographicData,
    isLoading: isDemographicsLoading,
    error: demographicsError,
  } = usePollDemographics({
    pollId: poll?.id || "",
  });

  // Get social metrics for this poll with realtime updates
  const { metrics: socialMetrics } = useSocialMetricsRealtime(
    poll?.id || "",
    "poll"
  );

  useEffect(() => {
    if (poll) {
      document.title = `${poll.title} | NationCard™ Poll`;
    } else {
      document.title = "Poll | NationCard™";
    }
  }, [poll]);

  // Handle comment click to switch to comments tab
  const handleCommentTabClick = () => {
    setActiveTab("comments");
    window.history.replaceState(
      null,
      "",
      `${window.location.pathname}#comments`
    );
  };

  // Check for hash in URL to switch tabs
  useEffect(() => {
    const hash = window.location.hash;
    if (hash) {
      const tabName = hash.replace("#", "");
      if (
        tabName === "comments" ||
        tabName === "results" ||
        tabName === "demographics"
      ) {
        setActiveTab(tabName);
      }
    }
  }, []);

  // If we have a canonical URL and we're not on it, redirect
  if (canonicalUrl && window.location.pathname !== canonicalUrl) {
    return <Navigate to={canonicalUrl} replace />;
  }

  // Function to get absolute URL for image
  const getAbsoluteImageUrl = (relativeUrl: string | null | undefined) => {
    if (!relativeUrl) return null;

    // If the URL is already absolute, return it
    if (relativeUrl.startsWith("http")) {
      return relativeUrl;
    }

    // Otherwise, make it absolute
    const origin = window.location.origin;
    return `${origin}${relativeUrl.startsWith("/") ? "" : "/"}${relativeUrl}`;
  };

  // Default image for sharing when poll has no image
  const defaultShareImage =
    "https://nationcard.org/img/nationcard-choose-informed.png";

  // Get the appropriate image for sharing
  const shareImageUrl =
    getAbsoluteImageUrl(poll?.image_url) || defaultShareImage;

  return (
    <div className="flex min-h-screen flex-col">
      {poll && (
        <Helmet>
          <title>{poll.title} | NationCard™ Poll</title>
          <meta
            name="description"
            content={
              poll.description || "Vote on this poll and share your opinion"
            }
          />

          {/* Open Graph / Facebook */}
          <meta
            property="og:title"
            content={`${poll.title} | NationCard™ Poll`}
          />
          <meta
            property="og:description"
            content={
              poll.description || "Vote on this poll and share your opinion"
            }
          />
          <meta property="og:type" content="website" />
          <meta property="og:url" content={shareableUrl} />
          <meta property="og:image" content={shareImageUrl} />
          <meta property="og:image:width" content="1200" />
          <meta property="og:image:height" content="630" />

          {/* Twitter */}
          <meta name="twitter:card" content="summary_large_image" />
          <meta
            name="twitter:title"
            content={`${poll.title} | NationCard™ Poll`}
          />
          <meta
            name="twitter:description"
            content={
              poll.description || "Vote on this poll and share your opinion"
            }
          />
          <meta name="twitter:image" content={shareImageUrl} />

          <link rel="canonical" href={shareableUrl} />
        </Helmet>
      )}

      <Header />

      <main className="container mx-auto px-2 sm:px-4 py-8 sm:py-20 flex-1 overflow-x-hidden">
        <div className="max-w-4xl mx-auto w-full">
          {isLoading ? (
            <PollLoadingState />
          ) : error || !poll ? (
            <PollErrorState error={error} />
          ) : (
            <CommentCountProvider contentId={poll.id} contentType="poll">
              <PollPageHeader poll={poll} />

              <div className="grid grid-cols-1 gap-4 sm:gap-6 w-full">
                <PollVotingCard
                  polls={polls}
                  setPolls={setPolls}
                  userVotes={userVotes}
                  setUserVotes={setUserVotes}
                  currentPoll={poll}
                  hasVoted={hasVoted}
                  canVote={canVote}
                  socialMetrics={socialMetrics}
                  onCommentClick={handleCommentTabClick}
                  markRecentVote={markRecentVote}
                  onRefresh={refresh}
                />

                <PollContentTabs
                  activeTab={activeTab}
                  setActiveTab={setActiveTab}
                  poll={poll}
                  userVoteOptionId={userVoteOptionId}
                  votesDistribution={votesDistribution}
                  demographicData={demographicData}
                  isDemographicsLoading={isDemographicsLoading}
                  socialMetrics={socialMetrics}
                />

                {relatedCards.length > 0 && (
                  <PollRelatedInsights cards={relatedCards} />
                )}
              </div>
            </CommentCountProvider>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default PollPage;
